import { IconRefresh } from '@tabler/icons-react';
import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';

import { isDynamicImportError } from '../../utils/dynamic-import-reload';
import { Button } from '../elements/button';
import { Fullscreen } from '../layout/fullscreen';

const getRouteErrorMessage = (error: unknown): string => {
  if (isRouteErrorResponse(error)) {
    return `${error.status} ${error.statusText}`;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Onbekende fout';
};

export const RouteErrorBoundary = () => {
  const error = useRouteError();
  const isDynamicImportFailure = isDynamicImportError(error);
  const isOffline = typeof window !== 'undefined' && window.navigator.onLine === false;
  const showTechnicalDetails = typeof window !== 'undefined' && window.location.hostname === 'localhost';

  useEffect(() => {
    console.error('Route error:', error);
  }, [error]);

  const title = isOffline
    ? 'Geen verbinding'
    : isDynamicImportFailure
      ? 'De app kon niet volledig laden'
      : 'Er is iets misgegaan';

  const description = isOffline
    ? 'Controleer je internetverbinding en vernieuw de pagina zodra je weer online bent.'
    : isDynamicImportFailure
      ? 'Vernieuw de pagina om de app opnieuw te laden en verder te gaan.'
      : 'Vernieuw de pagina en probeer het opnieuw.';

  return (
    <Fullscreen className="bg-brand-surface-blue px-4 py-6">
      <section className="w-full max-w-md rounded-[var(--radius-brand-panel)] border border-brand-blue-700/10 bg-white p-6 text-center shadow-sm">
        <h1 className="text-2xl font-semibold text-brand-blue-700">{title}</h1>
        <p className="mt-3 text-sm text-slate-600">{description}</p>

        <Button className="mt-6" leadingIcon={IconRefresh} onClick={() => window.location.reload()}>
          Pagina vernieuwen
        </Button>

        {showTechnicalDetails ? (
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-sm font-medium text-slate-700">Technische details</summary>
            <pre className="mt-2 overflow-auto rounded-md bg-slate-50 p-3 text-xs whitespace-pre-wrap text-slate-600">
              {getRouteErrorMessage(error)}
            </pre>
          </details>
        ) : null}
      </section>
    </Fullscreen>
  );
};
